/**
 * Alternate Section Background Color Configuration
 * 
 * This configuration allows you to customize alternating section backgrounds:
 * Section 1 (Banner): color1
 * Section 2 (Category Slider): color2
 * Section 3+: Alternate between color1 and color2
 * 
 * Example configurations:
 * 
 * Light Theme:
 * {
 *   color1: '#ffffff',  // White
 *   color2: '#f5f5f5',  // Light gray
 * }
 * 
 * Modern Theme:
 * {
 *   color1: '#fafafa',  // Off-white
 *   color2: '#f0f0f0',  // Medium gray
 * }
 */

// Default static configuration
const defaultAlternateSectionColorConfig = {
  // Color 1 - for odd sections (1, 3, 5, etc.)
  color1: '#F4EEE7',      // Warm beige background

  // Color 2 - for even sections (2, 4, 6, etc.)
  color2: '#ffffff',      // White background
};

// This will be set dynamically by the theme system
let alternateSectionColorConfig = { ...defaultAlternateSectionColorConfig };

/**
 * Get alternate section color configuration
 * @returns {object} The entire alternate section color configuration
 */
export const getAlternateSectionColorConfig = () => {
  return alternateSectionColorConfig;
};

/**
 * Get CSS custom properties for alternate section colors
 * @returns {object} CSS custom properties object
 */
export const getAlternateSectionColorCSSVariables = () => {
  return {
    '--section-color-1': alternateSectionColorConfig.color1,
    '--section-color-2': alternateSectionColorConfig.color2,
  };
};

/**
 * Get alternating color based on section index (0-based)
 * @param {number} sectionIndex - The zero-based index of the section
 * @returns {string} The color for this section
 */
export const getAlternateSectionColor = (sectionIndex) => {
  return sectionIndex % 2 === 0 
    ? alternateSectionColorConfig.color1 
    : alternateSectionColorConfig.color2;
};

/**
 * Update alternate section color configuration from theme data
 * @param {object} themeData - Theme data with landingSection object
 */
export const updateAlternateSectionColorConfigFromTheme = (themeData) => {
  if (themeData && themeData.landingSection) {
    alternateSectionColorConfig = {
      color1: themeData.landingSection.color1 || defaultAlternateSectionColorConfig.color1,
      color2: themeData.landingSection.color2 || defaultAlternateSectionColorConfig.color2,
    };
  }
};

export { alternateSectionColorConfig as default };
